import { useCallback, useEffect, useState } from 'react';
import type { Ad } from '@models/Ad.model';
import AdTagsReadOnly from '@/modules/createEditor/components/AdTagsReadOnly';
import CustomizationRequests from '@/modules/createEditor/components/CustomizationRequests';
import ReviewCommentList from '@/modules/createEditor/components/ReviewCommentList';

/*
    What the client asked for, beside the canvas (MPN-1631).

    The content team makes the requested edits here, but the requests and the review
    comments only existed on the Ad view, so every change meant reading one screen and
    typing in another.

    Deliberately a composition of the three components the Ad view already renders rather
    than three new ones. They are the same information; two implementations of "show this
    ad's comments" drift, and the ticket's whole point is that the two surfaces agree.
    ReviewCommentList already carries the composer and its "Visible to client" checkbox,
    so the add-comment half of this ticket is a prop, not a feature.

    What varies by context is only which sections apply:

      rebranded GuidePost (Reach or Create) -- tags, customization requests, comments
      Content Catalog template              -- tags only

    A template has neither of the other two by construction: MPN-1377 makes a rebrand a
    copy, so requests and comments attach to the copy and never to the original. Keyed on
    is_template rather than on the data being empty, because "no comments yet" and
    "comments do not apply here" are different claims and only the second should take the
    composer away.
*/

interface EditorSidebarProps {
  ad: Ad | null;
  /** Collapsed by the shell's toggle; unmounting would refetch on every reopen. */
  hidden?: boolean;
}

export function EditorSidebar({ ad, hidden = false }: EditorSidebarProps) {
  const isTemplate = ad?.is_template === true;
  const [refreshKey, setRefreshKey] = useState(0);

  // Someone can comment from the Ad view while this editor sits open for an hour, so the
  // sidebar refetches when the tab is focused again rather than showing what was true
  // when Kinema was opened. Cheap, needs no socket, and matches when a person would
  // actually look. Not a live feed -- see the ticket's open question.
  // No `hidden` guard: a collapsed sidebar renders nothing, so there is nothing to
  // refetch either way, and the guard was a line that could not be observed to work.
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  useEffect(() => {
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refresh]);

  if (hidden || !ad) return null;

  return (
    <aside className="kinema-sidebar" data-testid="kinema-sidebar">
      <AdTagsReadOnly key={`tags-${refreshKey}`} adId={ad.id} />

      {!isTemplate && (
        <>
          <CustomizationRequests key={`cr-${refreshKey}`} adId={ad.id} />
          <ReviewCommentList rootAd={ad} refreshKey={refreshKey} canComment />
        </>
      )}
    </aside>
  );
}
